import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Form,
  Tabs,
  Spin,
  Row,
  Badge,
} from 'antd';
import PropTypes from 'prop-types';
import { Trie } from 'ontraccr-common';

import colors from '../constants/Colors';

import Permissions from '../auth/Permissions';

import UserInfo from './UserInfo';
import UserLabels from './UserLabels';
import UserFiles from './UserFiles';
import Notes from '../notes/Notes';

import {
  getUserFiles,
  addNote,
  getNotes,
  getMyUserTags,
  markUserTagsAsRead,
} from './state/users.actions';
import { getIdMap, generateRandomString } from '../helpers/helpers';
import BoardLinkView from '../boards/BoardLinkView';
import Certifications from '../certifications/Certifications';
import { hasViewPermission, canAddUserCertifications } from './userHelpers';
import AssignedForms from '../forms/AssignedForms/AssignedForms';
import { getTemplates } from '../forms/state/forms.actions';

const { TabPane } = Tabs;

const tabStyle = {
  minHeight: '100%',
  width: '100%',
};

const CERT_TYPE = 'users';
export default function UserAddView(props) {
  const dispatch = useDispatch();

  const [
    userFiles,
    userNotes,
    labels,
    cardLinks,
    users,
  ] = useSelector((state) => [
    state.users.userFiles,
    state.users.notes,
    state.labels.filter((label) => label.type === 'users'),
    state.boards.cardLinks,
    state.users.users,
  ], shallowEqual);

  const myUserTags = useSelector((state) => state.users.myUserTags || []);

  const labelIdMap = useMemo(() => getIdMap(labels), [labels]);

  const userTrie = useMemo(() => {
    const trie = {};
    users.forEach((user) => {
      if (user.active && hasViewPermission(user)) {
        Trie.addToTrie(trie, user);
      }
    });
    return trie;
  }, [users]);

  const {
    company: {
      settings: {
        suggestUsernames = false,
        optionalUserEmail = false,
      } = {},
    } = {},
  } = useSelector((state) => state.settings);
  const [loading, setLoading] = useState(false);

  const {
    formRef,
    isAdd,
    userDivisions = {},
    userToLabel = {},
    user = {},
    errors,
    customFields,
    setCustomFields,
  } = props;
  const {
    name,
    username,
    email,
    phoneNumber,
    wage,
    position,
    editing = false,
    projects = [],
    teams = [],
    id,
    employeeId,
    union = {},
    scheduleColor,
    address,
    lat,
    lng,
    otProfileId,
  } = user;
  const {
    [id]: divisions = [],
  } = userDivisions;

  const {
    [id]: userLabels = [],
  } = userToLabel;

  const files = useMemo(() => {
    const {
      [id]: stateFiles = [],
    } = userFiles;
    return stateFiles;
  }, [userFiles, id]);

  const initialLabels = useMemo(() => (
    userLabels.map((labelId) => labelIdMap[labelId]).filter((label) => label)
  ), [userLabels, labelIdMap]);

  const ourLinks = useMemo(() => {
    if (!id || editing) return [];
    return cardLinks.filter((cardLink) => (
      cardLink.userId === id
    ));
  }, [cardLinks, id, editing]);

  const initialValues = {
    name,
    username,
    email,
    phoneNumber,
    wage,
    position,
    projects: projects.map((p) => p.name),
    teams: teams.map((t) => t.name),
    employeeId,
    divisions,
    labels: initialLabels,
    files,
    union,
    scheduleColor: scheduleColor || `${colors.ONTRACCR_GRAY}FF`,
    address,
    lat,
    lng,
    otProfileId: otProfileId || undefined,
  };

  if (isAdd && suggestUsernames) {
    initialValues.username = generateRandomString();
    initialValues.password = generateRandomString(true);
    initialValues.confirmPassword = initialValues.password;
  }

  const isNotDisplay = (isAdd || editing);
  const hasFileRead = Permissions.has('FILES_READ');
  const hasFormsClearPermissions = Permissions.has('FORMS_CLEAR');
  const canViewCertifications = hasViewPermission(user);
  const canAddCertifications = canAddUserCertifications(user);

  const unreadNoteIds = useMemo(() => {
    if (!id) return new Set();
    return new Set(
      myUserTags
        .filter((tag) => tag.userId === id)
        .map((tag) => tag.userNoteId),
    );
  }, [myUserTags, id]);

  const onTabChange = useCallback((activeTab) => {
    if (activeTab === 'notes' && unreadNoteIds.size > 0) {
      dispatch(markUserTagsAsRead(Array.from(unreadNoteIds)));
    }
  }, [dispatch, markUserTagsAsRead, unreadNoteIds]);

  useEffect(() => {
    const getFiles = async () => {
      setLoading(true);
      await dispatch(getUserFiles(id));
      setLoading(false);
    };
    if (dispatch && id) getFiles();
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getTemplates());
  }, []);

  useEffect(() => {
    dispatch(getMyUserTags());
  }, [dispatch]);

  if (loading) {
    return (
      <Row justify="center" style={{ marginTop: '30%', width: '100%' }}>
        {' '}
        <Spin />
        {' '}
      </Row>
    );
  }

  return (
    <Form
      layout="vertical"
      ref={formRef}
      initialValues={initialValues}
      // hack to make form reload when files are loaded into state for the user.
      key={id + files.length}
      className="user-add-form"
    >
      {
        isNotDisplay
          ? (
            <UserInfo
              form={props}
              user={user}
              optionalUserEmail={optionalUserEmail}
              errors={errors}
              customFields={customFields}
              setCustomFields={setCustomFields}
              divisions={divisions}
            />
          ) : (
            <Tabs
              key={id}
              tabBarStyle={{ marginBottom: 10 }}
              onChange={onTabChange}
            >
              <TabPane tab="General Info" key="general" style={tabStyle}>
                <UserInfo
                  form={props}
                  user={user}
                  errors={errors}
                  customFields={customFields}
                  setCustomFields={setCustomFields}
                  divisions={divisions}
                />
              </TabPane>
              <TabPane tab="Labels" key="labels" style={tabStyle}>
                <UserLabels userId={id} />
              </TabPane>
              {hasFileRead && (
              <TabPane tab="Files" key="files" style={tabStyle}>
                <UserFiles userId={id} />
              </TabPane>
              )}
              <TabPane
                tab={
                  <div style={{ paddingRight: unreadNoteIds.size > 0 ? 15 : 0 }}>
                    <Badge count={unreadNoteIds.size} offset={[5, -5]}>
                      Notes
                    </Badge>
                  </div>
                }
                key="notes"
                style={tabStyle}
              >
                <Notes
                  id={id}
                  notes={userNotes}
                  addNote={addNote}
                  getNotes={getNotes}
                  headerStyle={{ paddingLeft: 24 }}
                  authorKey="authorId"
                  users={users}
                  userTrie={userTrie}
                  allowTags
                  unreadSet={unreadNoteIds}
                />
              </TabPane>
              {ourLinks.length > 0 && (
              <TabPane tab="Cards" key="cards" style={tabStyle}>
                <BoardLinkView links={ourLinks} linkType="userId" linkId={id} />
              </TabPane>
              )}
              {canViewCertifications && (
                <TabPane tab="Certifications" key="certifications" style={tabStyle}>
                  <Certifications
                    id={id}
                    entityType={CERT_TYPE}
                    isDisplay={!canAddCertifications}
                  />
                </TabPane>
              )}
              {hasFormsClearPermissions && (
                <TabPane tab="Assigned Forms" key="assignedForms" style={tabStyle}>
                  <AssignedForms isUserAssigned userId={id} />
                </TabPane>
              )}
            </Tabs>
          )
      }
    </Form>
  );
}

UserAddView.propTypes = {
  formRef: PropTypes.shape({}).isRequired,
  isAdd: PropTypes.bool,
  userDivisions: PropTypes.shape({}),
  userToLabel: PropTypes.shape({}),
  user: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    wage: PropTypes.number,
    position: PropTypes.string,
    editing: PropTypes.bool,
    projects: PropTypes.arrayOf(PropTypes.shape({})),
    teams: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.string,
    employeeId: PropTypes.string,
    union: PropTypes.shape({}),
    scheduleColor: PropTypes.string,
    address: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  errors: PropTypes.shape({}),
  customFields: PropTypes.arrayOf(PropTypes.shape({})),
  setCustomFields: PropTypes.func.isRequired,
};

UserAddView.defaultProps = {
  isAdd: false,
  userDivisions: {},
  userToLabel: {},
  user: {},
  errors: {},
  customFields: [],
};
